import {
    select as D3select,
    geoPath as D3geoPath,
    dispatch as D3dispatch,
    scaleLinear as D3scaleLinear,
    geoEquirectangular as D3geoEquirectangular,
    local as D3local,
    zoom as D3zoom,
    format as D3format,
    extent as D3extent,
} from 'd3';

import D3Tooltips from './D3Tooltips';
import D3PulsatingDot from './D3PulsatingDot';
import D3AnimatedPath from './D3AnimatedPath';
import D3Button from './D3Button';
import { LetContextMenu } from './utils';

const RIGHT = 1;
const LEFT = 2;
const BOTH = 3;

const D3Geomap = function () {
    const features = [];
    const labels = {};

    // default settings can be overridden
    let width = 100;
    let wheight = 100;
    let clear = false;
    let live = false;
    let theme;

    const dispatch = D3dispatch('click');

    const dotscale = D3scaleLinear().range([4, 10]);
    const linescale = D3scaleLinear().range([2, 8]);
    const tip = D3Tooltips();
    const projection = D3geoEquirectangular();
    const path = D3geoPath();
    const pulsatingDot = D3PulsatingDot();
    const animatedPath = D3AnimatedPath();
    const knownTraffic = D3local();

    let colors = [];
    const nf = D3format(',.0f');
    let txfm = { x: 0, y: 0, k: 1 };
    let cselection = {};

    // helpers
    const scalefn = D3geoEquirectangular().scale(200);
    const getScale = () => txfm && txfm.k >= 1 ? scalefn.scale(txfm.k).scale() : 1;
    const getCount = c => c.actual > 0 ? c.actual : c.known;
    const getCountStr = c => c.actual > 0 ? 'Total' : 'known';

    function my (selection) {
        if (!selection) return;
        selection.each(function (data, i) {
            if (features.length < 1) return;
            const svg = D3select(this);
            let pinning = Object.entries(cselection).length > 0;

            // update map size
            const height = wheight > (width * 0.6) ? (width * 0.6) : wheight;
            const world = features.find(f => f.key === 'countries');
            if (!world) {
                console.log('countries is not optional');
                return;
            }
            projection.fitSize([width, height], world);
            // projection.fitHeight(height, world);
            path.projection(projection);
            tip
                .arrow(false)
                .theme(theme)
                .bounds({ width, height });
            const zoomed = (e) => {
                txfm = e.transform;
                _zoomed();
            };
            const zoom = D3zoom()
                .scaleExtent([1, 42]) // limit scale
                .translateExtent([[0, 0], [width, height]]) // limit pan area
                .on('zoom', zoomed);

            svg.attr('width', width)
                .attr('height', height)
                // prevent zoom pan on ctrl-click
                // someone forgot to tell the d3 guy to leave the secondary mouse event to the browser
                .on('mousedown', LetContextMenu)
                .on('click', (e, d) => {
                    // deselect pinned nodes
                    pinning = false;
                    tip.hide();
                    dispatch.call('click', my, {});
                })
                .call(zoom);
            // .on('wheel.zoom', null); // uncomment to disable wheel zoom

            let brcgeomap = svg.selectAll('.brcgeomap').data([data], d => d.key);
            brcgeomap.exit().remove();
            brcgeomap = brcgeomap.enter()
                .append('g')
                .attr('class', 'brcgeomap')
                .merge(brcgeomap);

            // filter for glowing land
            let filter = brcgeomap.selectAll('defs').data([data], d => d.key);
            filter = filter.enter().append('defs')
                .append('filter')
                .attr('id', 'glow');
            filter.append('feGaussianBlur')
                .attr('stdDeviation', '7')
                .attr('result', 'coloredBlur');
            const feMerge = filter.append('feMerge');
            feMerge.append('feMergeNode').attr('in', 'coloredBlur');
            feMerge.append('feMergeNode').attr('in', 'SourceGraphic');

            // add map features
            let mapg = brcgeomap.selectAll('.map').data([data], d => d.key);
            mapg = mapg.enter()
                .append('g').attr('class', 'map').attr('name', 'zoom')
                .merge(mapg);

            // map labels are added on top of all other features
            let maplg = brcgeomap.selectAll('.map-labels').data([data], d => d.key);
            maplg = maplg.enter()
                .append('g').attr('class', 'map-labels')
                .merge(maplg);

            // then links
            let linksg = brcgeomap.selectAll('.links').data([data], d => d.key);
            linksg = linksg.enter()
                .append('g').attr('class', 'links')
                .merge(linksg);

            // then nodes
            let nodesg = brcgeomap.selectAll('.nodes').data([data], d => d.key);
            nodesg = nodesg.enter()
                .append('g').attr('class', 'nodes')
                .merge(nodesg);

            // finally tooltips above everything
            const tipg = brcgeomap.selectAll('.tipg').data([data], d => d.key);
            tipg.enter()
                .append('g').attr('class', 'tipg')
                .merge(tipg)
                .call(tip);

            // zoom buttons
            const h = isNaN(wheight) || wheight > height - 50 ? height - 50 : wheight - 50;
            // you can grab icon paths from http://materialdesignicons.com/
            const btns = [
                {
                    key: 'in',
                    path: 'M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z',
                    x: width - 30,
                    y: h - 19,
                    handler: (elm, d, i) => zoom.scaleBy(svg, 2)
                },
                {
                    key: 'out',
                    path: 'M19,13H5V11H19V13Z',
                    x: width - 30,
                    y: h,
                    handler: (elm, d, i) => zoom.scaleBy(svg, 0.5)
                }];

            const btnjoin = brcgeomap.selectAll('.map-button')
                .data(btns, d => d.key);
            btnjoin.enter()
                .append('g')
                .attr('class', 'map-button')
                .merge(btnjoin)
                .call(D3Button().on('press', (elm, d, i) => {
                    d.handler(elm, d, i);
                    // D3event.stopPropagation();
                }));
            btnjoin.exit().remove();

            // update scales
            dotscale.domain([0, D3extent(Object.values(data.locations || {}), d => getCount(d.count))[1]]);
            linescale.domain([0, D3extent(Object.values(data.traffic || {}), d => d.count)[1]]);

            pulsatingDot
                .theme(theme)
                .color(colors[0])
                .projection(projection)
                .scale(getScale())
                .on('mouseover', onNodeMouseover)
                .on('click', onNodeClick)
                .on('mouseout', onNodeMouseout);

            animatedPath
                .theme(theme)
                .color(colors[1])
                .projection(projection)
                .scale(getScale())
                .on('mouseover', onPathMouseover)
                .on('mouseout', onPathMouseout);

            // add/update location nodes
            if (clear) nodesg.selectAll('.node').remove();
            const njoin = nodesg.selectAll('.node')
                .data(Object.entries(data.locations || {})
                    .map(([key, value]) => ({ key, value }))
                    .map(d => {
                        return { ...d.value, ...{ size: dotscale(getCount(d.value.count)) } };
                    }), d => d.key);

            njoin.exit().remove();
            njoin.enter()
                .append('g')
                .attr('class', 'node')
                .merge(njoin)
                .call(pulsatingDot.animate(live));

            // fire missiles for new traffic
            const traffic = Object.entries(data.traffic || {})
                .map(([key, value]) => ({ key, value }))
                .filter(d => {
                    const nodes = d.key.split(',');
                    return nodes.length > 1;
                })
                .map(d => {
                    return {
                        ...{
                            path: d.value.direction === LEFT ? [d.value.path[1], d.value.path[0]] : d.value.path,
                            direction: d.value.direction
                        },
                        ...d
                    };
                });

            const tjoin = linksg.selectAll('.traffic-tracker')
                .data(traffic, d => d.key);
            tjoin.enter()
                .append('g')
                .attr('class', 'traffic-tracker')
                .merge(tjoin)
                .each(function (d) {
                    // fire missiles on newly discovered locations (Terra Nova)
                    // and on new traffic for existing subjects of the empire (uprisings)
                    const attacklevel = d.value.count - knownTraffic.get(this);
                    if (live && attacklevel > 0) {
                        const ljoin = D3select(this).selectAll('.new-traffic')
                            .data([d], d => d.key);
                        ljoin.enter()
                            .append('g')
                            .attr('class', '.new-traffic')
                            .merge(ljoin)
                            .call(animatedPath)
                            // fade new traffic away after 8 seconds
                            .transition().duration(attacklevel > 1
                                ? theme.transitions.duration.newTrafficLong
                                : theme.transitions.duration.newTrafficShort)
                            .remove();
                    }
                    knownTraffic.set(this, d.value.count);
                });
            tjoin.exit().remove();

            function drawFeatures () {
                const fjoin = mapg.selectAll('.feature').data(features.map(f => {
                    if (f.type !== 'FeatureCollection') return f;
                    const ret = { key: f.key, type: f.type };
                    ret.features = f.features.filter(ff => ff.properties.scalerank <= (txfm ? txfm.k : 1));
                    return ret;
                }), d => d.key);
                fjoin.enter()
                    .append('path').attr('class', d => 'feature ' + d.key)
                    .merge(fjoin)
                    .attr('d', path);
                fjoin.exit().remove();
            }

            function drawLabels () {
                Object.keys(labels).forEach(label => {
                    const lpos = d => {
                        const crdn = path.centroid(d);
                        const ptxf = positionTxfm(crdn);
                        return [crdn[0] + ptxf.dx, crdn[1] + ptxf.dy];
                    };
                    const scale = txfm ? txfm.k : 1;

                    let ldata = [];
                    if (labels[label].show) {
                        ldata = labels[label].data.features.filter(d => d.properties.LABELRANK < scale);
                    }

                    const lbljoin = maplg.selectAll('.feature-label.' + label)
                        .data(ldata, d => d.id);
                    lbljoin.enter()
                        .append('text').attr('class', 'feature-label ' + label)
                        .merge(lbljoin)
                        .attr('transform', d => `translate(${lpos(d)})`)
                        .attr('dy', '.35em')
                        .attr('pointer-events', 'none')
                        .text(d => d.id);
                    lbljoin.exit().remove();
                });
            }

            function nodeSelections () {
                // pulsatingDot = pulsatingDot.scale(getScale());
                pulsatingDot.scale(getScale());

                // update size and set bkgd class
                nodesg.selectAll('.node')
                    .classed('bkgd', d => Object.entries(cselection).length > 0 && !cselection[d.key])
                    .each(function (d) {
                        d.size = cselection[d.key] ? dotscale.range()[1] : dotscale(getCount(d.count));
                    })
                    .call(pulsatingDot.animate(live));

                // scale is applied to line width and to animation speed. filter and decorate.
                const tselection = Object.entries(data.traffic || {})
                    .map(([key, value]) => ({ key, value }))
                    .filter(d => {
                        const nodes = d.key.split(',');
                        return nodes.length > 1 && (cselection[nodes[0]] || cselection[nodes[1]]);
                    })
                    .map(d => {
                        return {
                            ...{
                                path: d.value.direction === LEFT ? [d.value.path[1], d.value.path[0]] : d.value.path,
                                direction: d.value.direction
                            },
                            ...d
                        };
                    });

                const ljoin = linksg.selectAll('.traffic')
                    .data(tselection, d => d.key);
                // some energy required to stop objects in motion
                ljoin.exit().selectAll('*').interrupt();
                ljoin.exit().selectAll('*').remove();
                ljoin.exit().remove();
                ljoin.enter()
                    .append('g')
                    .attr('class', 'traffic')
                    .merge(ljoin)
                    .call(animatedPath.scale(getScale()));
            }

            const joiner = dr => dr === BOTH ? ' ⇄ ' : dr === LEFT ? ' ← ' : ' → ';
            function connectionString (c, p) {
                // put the ips in geo order if needed
                let ips = c.key.split(',');
                let dir = c.value.direction;
                if (projection(p[0])[0] > projection(p[1])[0]) {
                    ips = [ips[1], ips[0]];
                    // flip direction
                    dir = dir === BOTH ? dir : dir === RIGHT ? LEFT : RIGHT;
                }
                return ips.join(joiner(dir));
            }

            function linkSelections (key) {
                linksg.selectAll('.traffic')
                    .classed('bkgnd', d => key && d.key !== key)
                    .classed('frgnd', d => key && d.key === key);
            }

            function _zoomed () {
                const s = getScale();
                drawFeatures();
                drawLabels();
                mapg.attr('transform', txfm);
                mapg.selectAll('.feature').style('strokeWidth', `${1 / s}px`);
                nodesg.attr('transform', txfm);
                linksg.attr('transform', txfm);

                linksg.selectAll('path.traffic').remove();
                linksg.selectAll('path.new-traffic').remove();
                nodeSelections();
            }

            // get current zoom transformation for position [x,y]
            function positionTxfm (p) {
                if (p.length < 2) return { dx: 0, dy: 0 };
                const z = getScale();
                return {
                    dx: p[0] * (z - 1) + txfm.x,
                    dy: p[1] * (z - 1) + txfm.y
                };
            }

            // mouse event handlers
            function onNodeMouseover (d) {
                // figure out tip position given current zoom + pan transformation
                const tiptxfm = positionTxfm(projection(d.crdn));
                // show tip with ip addresses
                tip.move(
                    projection(d.crdn)[0] + tiptxfm.dx,
                    projection(d.crdn)[1] + 25 + tiptxfm.dy,
                    [[d.localstr,
                        nf(getCount(d.count)) + ' ' + getCountStr(d.count) + ' Activity']]
                        .concat(Object.entries(d.nodes || {})
                            .map(([key, value]) => ({ key, value }))
                            .sort((a, b) => getCount(b.value) - getCount(a.value))
                            .slice(0, 10)
                            .map(ip => [ip.key, nf(getCount(ip.value))]))
                );
                cselection = pinning ? cselection : {};
                cselection[d.key] = cselection[d.key] || 'selected';
                nodeSelections();
            }

            function onNodeClick (d) {
                // dispatch on click event in case caller wants to show a popup menu
                const node = D3select(d.node)._groups[0];
                dispatch.call('click', my, {
                    ips: Object.entries(d.nodes || {}).map(([key, value]) => key),
                    elt: node,
                    key: d.key,
                });
                // D3event.stopPropagation();
            }

            function onNodeMouseout (d) {
                tip.hide();
                if (cselection[d.key] !== 'pinned') {
                    delete cselection[d.key];
                }
                nodeSelections();
            }

            function onPathMouseover (d) {
                if (!pinning) return;
                // show tooltip in the midpoint of the line
                const mid = d.node.getPointAtLength(d.node.getTotalLength() / 2);
                // figure out tip position given current zoom + pan transformation
                const tiptxfm = positionTxfm([mid.x, mid.y]);
                tip.move(mid.x + tiptxfm.dx, mid.y + 25 + tiptxfm.dy,
                    Object.entries(d.value.connections)
                        .map(([key, value]) => ({ key, value }))
                        .sort((a, b) => b.value.count - a.value.count)
                        .slice(0, 10)
                        .map(c => [connectionString(c, d.value.path), nf(c.value.count)]));

                linkSelections(d.key);
            }

            function onPathMouseout (d) {
                linkSelections();
                tip.hide();
            }

            // finally apply current selections
            drawFeatures();
            drawLabels();
            nodeSelections();
        });
    }

    // getters/setters
    my.topology = function (key, show, data) {
        if (!arguments.length) return features;

        const i = features.findIndex(f => f.key === key);
        if (!show) {
            if (i >= 0) {
                features.splice(i, 1);
            }
            return my;
        }

        data.key = key;
        if (i >= 0) {
            features[i] = data;
        } else {
            features.push(data);
        }
        features.sort((a, b) => b.level - a.level);
        return my;
    };

    // options can be: country-names
    my.label = function (key, show, data) {
        labels[key] = { show, data };
        return my;
    };

    my.width = function (v) {
        if (!arguments.length) return width;
        width = v;
        return my;
    };

    my.wheight = function (v) {
        if (!arguments.length) return wheight;
        wheight = v;
        return my;
    };

    my.colors = function (v) {
        if (!arguments.length) return colors;
        colors = v;
        return my;
    };

    my.theme = function (v) {
        if (!arguments.length) return theme;
        theme = v;
        return my;
    };

    my.clear = function (v) {
        if (!arguments.length) return clear;
        clear = v;
        return my;
    };

    my.live = function (v) {
        if (!arguments.length) return live;
        live = v;
        return my;
    };

    my.cselection = function (v) {
        if (!arguments.length) return cselection;
        cselection = v || cselection;
        return my;
    };

    my.on = function () {
        const value = dispatch.on.apply(dispatch, arguments);
        return value === dispatch ? my : value;
    };

    return my;
};

export default D3Geomap;
